import type React from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NodeType, useIntrospectedPermissionsQuery } from "src/generated/graphql";
import { type ChildrenProps, Log } from "src/helpers";
import { useAppStore } from "src/state/appStore";

type introspectionContextType = {
	accessData: Set<string>;
	setAccessData: React.Dispatch<React.SetStateAction<Set<string>>>;
	introspectionError: boolean;

	isPartnerUser: boolean;

	noAccess: () => boolean;

	userCanSeePackages: () => boolean;
	userCanSeeAssets: () => boolean;
	userCanModifyDevices: () => boolean;
	userCanSeePeople: () => boolean;
	userCanSeeDevices: () => boolean;
	userCanModifyPeople: () => boolean;
	userCanViewAuditLogs: () => boolean;
	userCanModifyApplications: () => boolean;
	userCanModifyOrganisation: () => boolean;
	userCanSeeIntegrationEdge: () => boolean;
	userCanModifyIntegrationEdge: () => boolean;
	userCanRebootDevice: () => boolean;
	userCanRestartApp: () => boolean;
};

const introspectionContext = createContext<introspectionContextType | null>(null);

export const useAegisIntrospection = (): introspectionContextType => {
	const ctx = useContext(introspectionContext);
	if (ctx === null) {
		throw new Error("useAegisIntrospection must be used within a AegisIntrospectionProvider tag");
	}
	return ctx;
};

export const AegisIntrospectionProvider: React.FC<ChildrenProps> = ({ children }) => {
	const tenantId = useAppStore(s => s.tenantId);
	const setCurrentRoles = useAppStore(s => s.setCurrentRoles);

	const currentUserNodeType = useAppStore(s => s.currentUser?.nodeType);
	const rootNodeType = useAppStore(s => s.rootNode?.nodeType);

	const isPartnerUser = currentUserNodeType === NodeType.Partner || rootNodeType === NodeType.Partner;

	const [accessData, setAccessData] = useState<Set<string>>(new Set<string>());
	const [introspectionError, setIntrospectionError] = useState<boolean>(false);

	const { data, error, loading } = useIntrospectedPermissionsQuery({
		skip: !tenantId,
		variables: { tenantId: tenantId as string },
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true
	});

	const didApplyRef = useRef(false);

	useEffect(() => {
		if (!tenantId) return;
		if (loading) return;
		if (error) return;

		const list = data?.introspectedPermissions;
		if (!list) return;

		if (didApplyRef.current) return;
		didApplyRef.current = true;

		const accessSet = new Set<string>();
		for (const p of list) {
			if (p?.name) accessSet.add(p.name);
		}

		setAccessData(accessSet);
		setCurrentRoles(accessSet);
		setIntrospectionError(false);
	}, [tenantId, data, loading, error, setCurrentRoles]);

	useEffect(() => {
		didApplyRef.current = false;
	}, [tenantId]);

	useEffect(() => {
		if (!error) return;
		setIntrospectionError(true);
		Log.error(error);
	}, [error]);

	const noAccess = () => accessData.size === 0;

	const userCanSeePackages = () => {
		const p = accessData;
		return p.has("application.admin") || p.has("application.edit") || p.has("application.view");
	};
	const userCanSeeAssets = () => {
		const p = accessData;
		return p.has("asset.admin") || p.has("asset.edit") || p.has("asset.view");
	};
	const userCanSeeDevices = () => {
		const p = accessData;
		return p.has("device.admin") || p.has("device.edit") || p.has("device.view");
	};
	const userCanModifyDevices = () => {
		const p = accessData;
		return p.has("device.edit") || p.has("device.admin");
	};
	const userCanSeePeople = () => {
		const p = accessData;
		return p.has("people.admin") || p.has("people.edit") || p.has("people.view");
	};
	const userCanModifyPeople = () => {
		const p = accessData;
		return p.has("people.admin") || p.has("people.edit");
	};
	const userCanSeeIntegrationEdge = () => {
		const p = accessData;
		return p.has("integrationedge.view") || p.has("integrationedge.edit") || p.has("integrationedge.admin");
	};
	const userCanModifyIntegrationEdge = () => {
		const p = accessData;
		return p.has("integrationedge.edit") || p.has("integrationedge.admin");
	};
	const userCanViewAuditLogs = () => accessData.has("audit.view");
	const userCanModifyApplications = () => {
		const p = accessData;
		return p.has("application.admin") || p.has("application.edit");
	};
	const userCanModifyOrganisation = () => {
		const p = accessData;
		return p.has("organisation.admin") || p.has("organisation.edit");
	};
	const userCanRebootDevice = () => accessData.has("device.command.reboot");
	const userCanRestartApp = () => accessData.has("device.command.restart");

	const contextValue = useMemo(
		() =>
			({
				accessData,
				setAccessData,
				introspectionError,
				isPartnerUser,
				noAccess,
				userCanSeePackages,
				userCanSeeAssets,
				userCanSeeDevices,
				userCanModifyDevices,
				userCanSeePeople,
				userCanModifyPeople,
				userCanViewAuditLogs,
				userCanModifyApplications,
				userCanModifyOrganisation,
				userCanSeeIntegrationEdge,
				userCanModifyIntegrationEdge,
				userCanRebootDevice,
				userCanRestartApp
			}) satisfies introspectionContextType,
		[accessData, introspectionError, isPartnerUser]
	);

	return <introspectionContext.Provider value={contextValue}>{children}</introspectionContext.Provider>;
};